// Left-column composer for the Roadmap view. Hosts three sections:
// roadmaps (dropdown), projects (multi-select checkboxes), team change
// (single-select radios), and a Create / Update Roadmap CTA panel
// that surfaces when the selection has drifted from the persisted
// roadmap (or no roadmap is active and the user has free-form
// selected projects / a team change). The Resource / Impact / Diff
// view toggle lives in the top floating toolbar (see RoadmapView).

import type { Roadmap } from '@lib/api-types/roadmaps';
import type { RoadmapView as ViewPayload } from '@lib/api-types/views';
import { useState, type Dispatch } from 'react';

import { Select } from '../../../components/Select.tsx';
import { EmptyState } from '../../components/EmptyState.tsx';
import {
  activeRoadmap,
  canCreateRoadmap,
  isProjectSelected,
  isRoadmapDirty,
  isTeamChangeSelected,
  type RoadmapAction,
  type RoadmapState,
} from '../../roadmap-data.ts';


/**
 * Structural row the Projects + Team change sections render against.
 * Plans (saved scaffolds in the `plans` D1 table) and the legacy
 * `view.projects` / `view.team_changes` arrays both reduce to this
 * shape — the sidebar doesn't care about their internal payload.
 *
 * `isPlan` flips the edit/delete affordances on. Mock-data legacy
 * entries don't get them (they're not user-authored).
 */
interface SidebarItem {
  id: string;
  display: string;
  isPlan: boolean;
}

export function RoadmapSidebar({
  view,
  state,
  dispatch,
  onCreateProject,
  onCreateTeamChange,
  onEditPlan,
  onDeletePlan,
  onCreateRoadmap,
  onUpdateRoadmap,
  isSavingRoadmap,
}: {
  view: ViewPayload;
  state: RoadmapState;
  dispatch: Dispatch<RoadmapAction>;
  onCreateProject: () => void;
  onCreateTeamChange: () => void;
  /** Reopen the create-project / new-team-change scaffold pre-populated with the plan's payload for editing. */
  onEditPlan: (planId: string, kind: 'project' | 'team_change') => void;
  /** Soft-delete the plan via DELETE /plans/:id after confirming. */
  onDeletePlan: (planId: string, kind: 'project' | 'team_change', display: string) => void;
  /** Submit handler for the Create Roadmap CTA. Receives the user's chosen display name. */
  onCreateRoadmap: (display: string) => void;
  /** Submit handler for the Update Roadmap CTA. Persists the current selection onto `rm`. */
  onUpdateRoadmap: () => void;
  /** Disable the CTA buttons while a mutation is in flight. */
  isSavingRoadmap: boolean;
}) {
  const rm = activeRoadmap(state, view.roadmaps);
  const hasRoadmaps = view.roadmaps.length > 0;

  // Merge legacy view.projects / view.team_changes with saved plans of
  // the matching kind. The real backend always returns the legacy
  // arrays empty (project + team-change ledger ops were retired in
  // PR #140); the mock-data marketing preview still populates them.
  // Saved scaffolds live in view.plans.
  const projectItems: SidebarItem[] = [
    ...view.projects.map(p => ({ id: p.id, display: p.display, isPlan: false })),
    ...view.plans
      .filter(p => p.kind === 'project')
      .map(p => ({ id: p.id, display: p.display, isPlan: true })),
  ];
  const teamChangeItems: SidebarItem[] = [
    ...view.team_changes.map(tc => ({ id: tc.id, display: tc.display, isPlan: false })),
    ...view.plans
      .filter(p => p.kind === 'team_change')
      .map(p => ({ id: p.id, display: p.display, isPlan: true })),
  ];

  return (
    <aside className='snap-roadmap__sidebar'>
      {hasRoadmaps ? (
        <RoadmapsSection
          roadmaps={view.roadmaps}
          activeId={rm?.id ?? null}
          onSelect={(roadmapId, roadmap) =>
            dispatch({ type: 'select-roadmap', roadmapId, roadmap })
          }
        />
      ) : null}

      <ProjectsSection
        projects={projectItems}
        state={state}
        dispatch={dispatch}
        onCreateProject={onCreateProject}
        onEditPlan={(id) => onEditPlan(id, 'project')}
        onDeletePlan={(id, display) => onDeletePlan(id, 'project', display)}
      />

      <TeamChangeSection
        teamChanges={teamChangeItems}
        state={state}
        dispatch={dispatch}
        onCreateTeamChange={onCreateTeamChange}
        onEditPlan={(id) => onEditPlan(id, 'team_change')}
        onDeletePlan={(id, display) => onDeletePlan(id, 'team_change', display)}
      />

      <RoadmapSavePanel
        canCreate={canCreateRoadmap(state)}
        // Pass the universe of currently-existing plan ids so the
        // dirty check ignores dangling refs (e.g. when the user just
        // deleted a plan that the active roadmap was referencing).
        // Deleting a plan on its own shouldn't surface the Update
        // Roadmap CTA.
        isDirty={isRoadmapDirty(state, view.roadmaps, {
          projects: new Set(projectItems.map((p) => p.id)),
          teamChanges: new Set(teamChangeItems.map((tc) => tc.id)),
        })}
        rm={rm}
        isSaving={isSavingRoadmap}
        onCreate={onCreateRoadmap}
        onUpdate={onUpdateRoadmap}
      />
    </aside>
  );
}

// ---- New project button ---------------------------------------------

function NewProjectButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type='button'
      className='snap-roadmap__new-project'
      onClick={onClick}
    >
      + New project
    </button>
  );
}

function NewTeamChangeButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type='button'
      className='snap-roadmap__new-project'
      onClick={onClick}
    >
      + New team change
    </button>
  );
}

// ---- Roadmaps (radio) -----------------------------------------------

function RoadmapsSection({
  roadmaps,
  activeId,
  onSelect,
}: {
  roadmaps: Roadmap[];
  activeId: string | null;
  /** Forwards the roadmap object alongside the id so the reducer can snapshot its projects + team_change. */
  onSelect: (id: string | null, roadmap: Roadmap | null) => void;
}) {
  return (
    <section className='snap-roadmap__section'>
      <h3 className='snap-roadmap__section-label'>Roadmaps</h3>
      <div className='snap-roadmap__section-body'>
        <Select
          label='Active roadmap'
          style={{ width: '100%' }}
          value={activeId ?? ''}
          onChange={e => {
            const id = e.target.value || null;
            const roadmap = id ? roadmaps.find(r => r.id === id) ?? null : null;
            onSelect(id, roadmap);
          }}
        >
          <option value=''>No roadmap (free-form selection)</option>
          {roadmaps.map(r => (
            <option key={r.id} value={r.id}>
              {r.display}
            </option>
          ))}
        </Select>
      </div>
    </section>
  );
}

// ---- Projects (checkboxes when roadmap active, radio when not) -------

function ProjectsSection({
  projects,
  state,
  dispatch,
  onCreateProject,
  onEditPlan,
  onDeletePlan,
}: {
  projects: SidebarItem[];
  state: RoadmapState;
  dispatch: Dispatch<RoadmapAction>;
  onCreateProject: () => void;
  onEditPlan: (id: string) => void;
  onDeletePlan: (id: string, display: string) => void;
}) {
  // Multi-select in both modes — when a roadmap is active, the
  // reducer's `select-roadmap` case has already seeded the live
  // selection from `rm.projects`; toggling here flips items in/out
  // of the working set, and the dirty diff drives the Update CTA.
  // With no roadmap active, the same checkboxes feed the Create
  // Roadmap CTA at the bottom of the sidebar.
  return (
    <section className='snap-roadmap__section'>
      <h3 className='snap-roadmap__section-label'>Projects</h3>
      <div className='snap-roadmap__section-body'>
        {projects.length === 0 ? (
          <EmptyState compact title='No projects yet' />
        ) : (
          projects.map(p => (
            <CheckboxRow
              key={p.id}
              label={p.display}
              checked={isProjectSelected(state, p.id)}
              onToggle={() =>
                dispatch({ type: 'toggle-project', projectId: p.id })
              }
              {...(p.isPlan ? { onEdit: () => onEditPlan(p.id) } : {})}
              {...(p.isPlan
                ? { onDelete: () => onDeletePlan(p.id, p.display) }
                : {})}
            />
          ))
        )}
        <NewProjectButton onClick={onCreateProject} />
      </div>
    </section>
  );
}

// ---- Team change -----------------------------------------------------

function TeamChangeSection({
  teamChanges,
  state,
  dispatch,
  onCreateTeamChange,
  onEditPlan,
  onDeletePlan,
}: {
  /** All available team changes (legacy + saved plans). */
  teamChanges: SidebarItem[];
  state: RoadmapState;
  dispatch: Dispatch<RoadmapAction>;
  onCreateTeamChange: () => void;
  onEditPlan: (id: string) => void;
  onDeletePlan: (id: string, display: string) => void;
}) {
  // A roadmap can carry at most one team-change, so this section is
  // always single-select (radio). When a roadmap is active, the
  // reducer has already seeded `selectedTeamChangeId` from
  // `rm.team_change`; clicking a different row swaps it, and the
  // dirty diff drives the Update Roadmap CTA. Clicking the
  // currently-selected row deselects (sets to null).
  return (
    <section className='snap-roadmap__section'>
      <h3 className='snap-roadmap__section-label'>Team change</h3>
      <div className='snap-roadmap__section-body'>
        {teamChanges.length === 0 ? (
          <EmptyState compact title='No team changes yet' />
        ) : (
          teamChanges.map(tc => (
            <RadioRow
              key={tc.id}
              label={tc.display}
              checked={isTeamChangeSelected(state, tc.id)}
              onSelect={() =>
                dispatch({
                  type: 'select-team-change',
                  teamChangeId: isTeamChangeSelected(state, tc.id) ? null : tc.id,
                })
              }
              {...(tc.isPlan ? { onEdit: () => onEditPlan(tc.id) } : {})}
              {...(tc.isPlan
                ? { onDelete: () => onDeletePlan(tc.id, tc.display) }
                : {})}
            />
          ))
        )}
        <NewTeamChangeButton onClick={onCreateTeamChange} />
      </div>
    </section>
  );
}

// ---- Roadmap save panel (Create / Update CTAs) -----------------------

function RoadmapSavePanel({
  canCreate,
  isDirty,
  rm,
  isSaving,
  onCreate,
  onUpdate,
}: {
  canCreate: boolean;
  isDirty: boolean;
  rm: Roadmap | null;
  isSaving: boolean;
  onCreate: (display: string) => void;
  onUpdate: () => void;
}) {
  const [nameInputOpen, setNameInputOpen] = useState(false);
  const [name, setName] = useState('');

  if (rm && isDirty) {
    return (
      <section className='snap-roadmap__section'>
        <button
          type='button'
          className='snap-roadmap__save-cta'
          disabled={isSaving}
          onClick={onUpdate}
        >
          {isSaving ? 'Updating…' : 'Update Roadmap'}
        </button>
      </section>
    );
  }

  if (!rm && canCreate) {
    if (nameInputOpen) {
      return (
        <section className='snap-roadmap__section'>
          <p className='snap-roadmap__save-hint'>Name this roadmap:</p>
          <form
            className='snap-roadmap__save-form'
            onSubmit={e => {
              e.preventDefault();
              const trimmed = name.trim();
              if (trimmed.length === 0) return;
              onCreate(trimmed);
              setName('');
              setNameInputOpen(false);
            }}
          >
            <input
              type='text'
              autoFocus
              value={name}
              placeholder='e.g. H2 2026'
              className='snap-roadmap__save-input'
              onChange={e => setName(e.target.value)}
              disabled={isSaving}
            />
            <div className='snap-roadmap__save-buttons'>
              <button
                type='submit'
                className='snap-roadmap__save-cta'
                disabled={isSaving || name.trim().length === 0}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type='button'
                className='snap-roadmap__save-cancel'
                onClick={() => {
                  setName('');
                  setNameInputOpen(false);
                }}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      );
    }
    return (
      <section className='snap-roadmap__section'>
        <button
          type='button'
          className='snap-roadmap__save-cta'
          disabled={isSaving}
          onClick={() => setNameInputOpen(true)}
        >
          Create Roadmap
        </button>
      </section>
    );
  }

  return null;
}

// ---- Primitives ------------------------------------------------------

function RadioRow({
  label,
  checked,
  muted,
  onSelect,
  onEdit,
  onDelete,
}: {
  label: string;
  checked: boolean;
  muted?: boolean;
  onSelect: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const classes = ['snap-roadmap__row'];
  if (checked) classes.push('is-active');
  if (muted) classes.push('is-muted');
  return (
    <RowWrapper onEdit={onEdit} onDelete={onDelete} label={label}>
      <button
        type='button'
        className={`${classes.join(' ')} snap-roadmap__row-main`}
        onClick={onSelect}
      >
        <span
          className={
            checked
              ? 'snap-roadmap__radio is-on'
              : 'snap-roadmap__radio'
          }
          aria-hidden
        />
        <span className='snap-roadmap__row-label'>{label}</span>
      </button>
    </RowWrapper>
  );
}

function CheckboxRow({
  label,
  checked,
  onToggle,
  onEdit,
  onDelete,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const classes = ['snap-roadmap__row'];
  if (!checked) classes.push('is-off');
  return (
    <RowWrapper onEdit={onEdit} onDelete={onDelete} label={label}>
      <button
        type='button'
        className={`${classes.join(' ')} snap-roadmap__row-main`}
        onClick={onToggle}
      >
        <span
          className={
            checked
              ? 'snap-roadmap__check is-on'
              : 'snap-roadmap__check'
          }
          aria-hidden
        />
        <span className='snap-roadmap__row-label'>{label}</span>
      </button>
    </RowWrapper>
  );
}

/**
 * Wraps a row + the optional edit / delete action icons that appear
 * on hover. The action buttons live as siblings of the primary
 * select button (not nested) so the markup stays valid: nesting
 * `<button>` inside `<button>` is invalid HTML and breaks keyboard
 * navigation.
 */
function RowWrapper({
  children,
  onEdit,
  onDelete,
  label,
}: {
  children: React.ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
  label: string;
}) {
  const hasActions = onEdit !== undefined || onDelete !== undefined;
  if (!hasActions) return <>{children}</>;
  return (
    <div className='snap-roadmap__row-wrapper'>
      {children}
      <div className='snap-roadmap__row-actions' aria-hidden>
        {onEdit ? (
          <button
            type='button'
            className='snap-roadmap__row-action'
            aria-label={`Edit ${label}`}
            title={`Edit ${label}`}
            onClick={onEdit}
          >
            <EditIcon />
          </button>
        ) : null}
        {onDelete ? (
          <button
            type='button'
            className='snap-roadmap__row-action snap-roadmap__row-action--danger'
            aria-label={`Delete ${label}`}
            title={`Delete ${label}`}
            onClick={onDelete}
          >
            <TrashIcon />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function EditIcon() {
  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M11.5 2.5l2 2-8 8H3.5v-2l8-8z' />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M3 4.5h10M6.5 4.5v-2h3v2M5 4.5l.7 8.5h4.6l.7-8.5' />
    </svg>
  );
}

